/**
 * Cards of one Kanban column (one droppable). Entry order comes from
 * layoutKanbanColumnCards; this component only renders it.
 */

import { KanbanCardItem } from './KanbanCardItem';
import { KanbanFadedCardPlaceholder } from './KanbanFadedCardPlaceholder';
import { KANBAN_PLACEHOLDER_FLIP_PREFIX } from './Kanban.motion';
import { layoutKanbanColumnCards } from './kanban.columnCards';
import type {
  KanbanDensity,
  KanbanDropPreview,
  KanbanItemBase,
  KanbanProps,
} from './Kanban.config.types';
import type { KanbanSettlingDrop } from './useKanbanDropSettle';

interface KanbanColumnCardsProps<TItem extends KanbanItemBase> {
  droppableId: string;
  columnItems: TItem[];
  activeItem: TItem | null;
  dropPreview: KanbanDropPreview | null;
  settling: KanbanSettlingDrop | null;
  settlingItem: TItem | null;
  renderCard: KanbanProps<TItem>['renderCard'];
  density: KanbanDensity;
  cardDragEnabled: boolean;
  selectionEnabled: boolean;
  isItemSelectable?: (item: TItem) => boolean;
  selectedIds: string[];
  onSelectedIdsChange?: (ids: string[]) => void;
}

export function KanbanColumnCards<TItem extends KanbanItemBase>({
  droppableId,
  columnItems,
  activeItem,
  dropPreview,
  settling,
  settlingItem,
  renderCard,
  density,
  cardDragEnabled,
  selectionEnabled,
  isItemSelectable,
  selectedIds,
  onSelectedIdsChange,
}: KanbanColumnCardsProps<TItem>) {
  const columnItemIds = columnItems.map((entry) => entry.id);
  const placeholderFlipKey = `${KANBAN_PLACEHOLDER_FLIP_PREFIX}${droppableId}`;
  const { slots, isSourceCollapsed } = layoutKanbanColumnCards({
    droppableId,
    columnItems,
    activeItem,
    dropPreview,
    settling,
    settlingItem,
  });

  return (
    <>
      {slots.map(({ kind, item, isDragSource }) =>
        kind === 'placeholder' ? (
          <KanbanFadedCardPlaceholder
            key="kanban-faded-placeholder"
            flipKey={placeholderFlipKey}
            flipFrom={item.id}
          >
            <div className="kanban-card-slot" data-kanban-card-slot="">
              {renderCard(item, {
                density,
                isSelected: false,
                selectionEnabled: false,
                onToggleSelect: () => undefined,
                selectionControl: null,
                isOverlay: false,
                columnItemIds: [],
              })}
            </div>
          </KanbanFadedCardPlaceholder>
        ) : (
          <KanbanCardItem
            key={item.id}
            isDragSourceCollapsed={isDragSource && isSourceCollapsed}
            flipFrom={
              isDragSource && !isSourceCollapsed ? placeholderFlipKey : undefined
            }
            item={item}
            renderCard={renderCard}
            density={density}
            columnItemIds={columnItemIds}
            cardDragEnabled={cardDragEnabled}
            selectionEnabled={selectionEnabled}
            isItemSelectable={isItemSelectable}
            selectedIds={selectedIds}
            onSelectedIdsChange={onSelectedIdsChange}
          />
        )
      )}
    </>
  );
}
